/**
 * Every outbound delivery, and the one action an operator has.
 *
 * Retry applies to a failed row only. The API refuses it on anything
 * else, so the button is absent there rather than present and rejected —
 * an action offered and then refused teaches the operator nothing.
 */
import { ApiError } from '@optum/api-client/client';
import {
  listWebhookDeliveries,
  retryWebhookDelivery,
} from '@optum/api-client/endpoints/webhook-deliveries';
import type { DeliveryEvent, DeliveryStatus, WebhookDelivery } from '@optum/api-client/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { absoluteDateTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { DataTable } from '../../ui/DataTable.js';
import { ListShape } from '../../ui/shapes.js';
import { AnnouncedStatus, DeliveryStatusBadge, EventBadge } from './delivery-badges.js';

const STATUSES: (DeliveryStatus | 'all')[] = ['all', 'pending', 'delivered', 'failed'];
const EVENTS: (DeliveryEvent | 'any')[] = ['any', 'task.status', 'task.output'];

export function DeliveriesPage() {
  const [status, setStatus] = useState<DeliveryStatus | 'all'>('all');
  const [event, setEvent] = useState<DeliveryEvent | 'any'>('any');
  const filters = {
    ...(status === 'all' ? {} : { status }),
    ...(event === 'any' ? {} : { event }),
  };

  const query = useQuery({
    queryKey: qk.webhookDeliveries(filters),
    queryFn: () => listWebhookDeliveries(filters),
  });

  return (
    <ListShape
      title="Webhook deliveries"
      subtitle="Every task's status and output events, sent to organization and partner endpoints."
    >
      <div
        style={{
          marginBottom: 11,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 7,
        }}
      >
        {STATUSES.map((value) => (
          <Button
            key={value}
            variant={status === value ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setStatus(value)}
          >
            {value}
          </Button>
        ))}
        <span
          style={{
            marginLeft: 5,
            marginRight: 5,
            fontSize: 'var(--text-meta-lg)',
            color: 'var(--text-muted)',
          }}
        >
          event
        </span>
        {EVENTS.map((value) => (
          <Button
            key={value}
            variant={event === value ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setEvent(value)}
          >
            {value}
          </Button>
        ))}
      </div>
      <Body query={query} />
    </ListShape>
  );
}

function Body({ query }: { query: ReturnType<typeof useQuery<WebhookDelivery[]>> }) {
  if (query.isPending) return <LoadingBlock />;
  if (query.isError) {
    return (
      <ErrorBlock message={query.error instanceof Error ? query.error.message : 'Failed to load'} />
    );
  }
  const rows = query.data ?? [];
  if (rows.length === 0) {
    return <EmptyBlock>No deliveries yet.</EmptyBlock>;
  }

  return (
    <DataTable
      rows={rows}
      columns={[
        {
          key: 'event',
          label: 'Event',
          render: (delivery) => <EventBadge event={delivery.event} />,
        },
        { key: 'sequence', label: 'Seq', mono: true },
        {
          key: 'task_id',
          label: 'Task',
          mono: true,
          render: (delivery) => (
            <Link to={`/tasks/${delivery.task_id}`} style={{ color: 'var(--text-accent)' }}>
              {delivery.task_id.slice(0, 8)}
            </Link>
          ),
        },
        {
          key: 'announced_status',
          label: 'Announced',
          render: (delivery) => <AnnouncedStatus value={delivery.announced_status} />,
        },
        {
          key: 'status',
          label: 'Delivery',
          render: (delivery) => <DeliveryStatusBadge status={delivery.status} />,
        },
        {
          key: 'target_kind',
          label: 'Target',
          render: (delivery) =>
            delivery.target_kind === 'organization' ? 'organization' : 'partner',
        },
        { key: 'attempts', label: 'Attempts', mono: true },
        {
          key: 'last',
          label: 'Last result',
          wrap: true,
          render: (delivery) => (
            <>
              {delivery.last_status_code ?? '—'}
              {delivery.last_error ? ` · ${delivery.last_error.slice(0, 60)}` : ''}
            </>
          ),
        },
        {
          key: 'when',
          label: 'When',
          mono: true,
          render: (delivery) => absoluteDateTime(delivery.delivered_at ?? delivery.created_at),
        },
        { key: 'retry', label: '', render: (delivery) => <RetryCell delivery={delivery} /> },
      ]}
    />
  );
}

function RetryCell({ delivery }: { delivery: WebhookDelivery }) {
  const qc = useQueryClient();
  const toast = useToast();
  const retry = useMutation({
    mutationFn: () => retryWebhookDelivery(delivery.id),
    onSuccess: () => {
      toast.success('Delivery requeued');
      void qc.invalidateQueries({ queryKey: ['webhook-deliveries'] });
    },
    onError: (e) => toast.error('Retry failed', e instanceof ApiError ? e.message : String(e)),
  });

  // Only a failed row can be retried; the API refuses the rest.
  if (delivery.status !== 'failed') return null;
  return (
    <Button size="sm" variant="secondary" onClick={() => retry.mutate()} disabled={retry.isPending}>
      {retry.isPending ? 'Retrying…' : 'Retry'}
    </Button>
  );
}
